import { useRef, useState } from "react";
import { apiRequest, uploadWithProgress, isAbortError } from "../../lib/apiClient";
import { useCollection } from "../../lib/useCollection";
import { useToast } from "../../components/ui/ToastProvider";
import { useConfirm } from "../../components/ui/ConfirmProvider";
import { AsyncSection } from "../../components/ui/AsyncSection";
import { FileField } from "../../components/ui/Field";

const FALLBACK_IMAGE = "/assets/sholay.jpeg";

export function HeroSlidesPanel() {
  const { status, data: slides, error, reload, setData } = useCollection(
    "/api/hero",
    (payload) => payload?.slides || []
  );

  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  // A ref, not document.querySelector('input[type="file"]') — that grabbed
  // whichever file input happened to render first in the whole document.
  const fileInputRef = useRef(null);
  const toast = useToast();
  const confirm = useConfirm();

  const handleUpload = async (event) => {
    event.preventDefault();

    if (!file) {
      toast.error("Choose an image first.");
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    setUploading(true);
    setProgress(0);

    try {
      await uploadWithProgress("/api/hero/add-slide", formData, { onProgress: setProgress });

      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Slide uploaded.");
      reload();
    } catch (err) {
      if (!isAbortError(err)) toast.error(err.message);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDelete = async (slide) => {
    const confirmed = await confirm({
      title: "Delete this slide?",
      message: "It will be removed from the homepage carousel and deleted from Cloudinary.",
    });
    if (!confirmed) return;

    try {
      await apiRequest(`/api/hero/delete-slide/${slide._id}`, { method: "DELETE", auth: true });
      setData((current) => current.filter((item) => item._id !== slide._id));
      toast.success("Slide deleted.");
    } catch (err) {
      if (!isAbortError(err)) toast.error(err.message);
    }
  };

  return (
    <section className="panel" aria-labelledby="hero-slides-heading">
      <div className="upload-card">
        <h2 id="hero-slides-heading">Homepage slides</h2>

        <form onSubmit={handleUpload}>
          <FileField
            label="Slide image"
            hint="Shown full-bleed behind the homepage hero. Maximum 20MB."
            inputRef={fileInputRef}
            onChange={setFile}
            disabled={uploading}
          />

          <button type="submit" disabled={uploading || !file}>
            {uploading ? `Uploading… ${progress}%` : "Upload slide"}
          </button>
        </form>

        {uploading && (
          <div className="progress-wrapper">
            <div
              className="progress-bar"
              style={{ width: `${progress}%` }}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Upload progress"
            />
            <span className="progress-text" aria-live="polite">{progress}%</span>
          </div>
        )}
      </div>

      <AsyncSection
        status={status}
        error={error}
        isEmpty={slides?.length === 0}
        emptyMessage="No slides yet. Upload one to fill the homepage hero."
        onRetry={reload}
      >
        <ul className="slides-grid">
          {slides?.map((slide, index) => (
            <li key={slide._id} className="slide-card">
              <img
                src={slide.image && slide.image !== "null" ? slide.image : FALLBACK_IMAGE}
                alt={`Homepage slide ${index + 1}`}
                loading="lazy"
                decoding="async"
                onError={(event) => { event.target.src = FALLBACK_IMAGE; }}
              />
              <button
                type="button"
                className="delete-btn"
                onClick={() => handleDelete(slide)}
                aria-label={`Delete homepage slide ${index + 1}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      </AsyncSection>
    </section>
  );
}
